import type { Cadeia, Caractere, IdPalavra, PalavraColocada } from '../tipos';
import { ALFABETO } from '../lexico/alfabeto';
import type { IndiceLexico } from '../lexico/indice';
import { caracteresCanonicos, paraCaractere } from '../lexico/normalizacao';
import { celulaNoIndice } from '../tabuleiro/coordenadas';
import type { Grade } from '../tabuleiro/grade';

/**
 * Regras do modo Átrio (§22.4).
 *
 * A pergunta que este modo responde é: *qual decisão o jogador já toma que o
 * Bistrô não cobra?* São duas, e as duas já estão na tela.
 *
 * **A escolha da âncora é uma aposta.** Sair por `A` é trivial; sair por `Z`,
 * `X` ou `Ç` é duro. No Bistrô as duas valem igual. Aqui a raridade da letra vira
 * o multiplicador da jogada seguinte: aposta-se antes de saber se dá para
 * entregar, e não entregar custa o turno.
 *
 * **O cruzamento múltiplo é a mecânica mais funda do jogo e é invisível.** O
 * ADR-0008 deixa uma palavra atravessar várias já escritas, com toda sequência
 * perpendicular tendo de ser palavra também. Isso é difícil, acontece por
 * acaso e ninguém percebe. Aqui cada travessia além da obrigatória paga.
 *
 * Tudo isto é função pura da cadeia e do índice: nada de relógio, nada de
 * aleatoriedade nova. A mesma partida pontua sempre igual (ADR-0004).
 */

/** Jogadas de uma partida do Átrio. */
export const JOGADAS_TRAMA = 10;

export const MULTIPLICADOR_MINIMO = 1;
export const MULTIPLICADOR_MAXIMO = 5;

/**
 * Pontos por travessia além da obrigatória.
 *
 * Vale mais que uma palavra média de propósito: atravessar duas palavras é o
 * que o modo quer ensinar, e pagar pouco por isso deixaria o jogo a ser um
 * concurso de palavras compridas.
 */
export const BONUS_CRUZAMENTO = 5;

/**
 * Faixas do multiplicador, por fração de alcance em relação à letra mais
 * comum do corpus. Degraus, e não uma curva contínua, porque o número aparece
 * na tela antes da aposta: `×3` se lê, `×2,7418` não.
 */
const FAIXAS: readonly { readonly piso: number; readonly multiplicador: number }[] = [
  { piso: 0.5, multiplicador: 1 },
  { piso: 0.2, multiplicador: 2 },
  { piso: 0.05, multiplicador: 3 },
  { piso: 0.01, multiplicador: 4 },
];

/**
 * Quantas candidatas do dicionário contêm a letra, somadas todas as posições.
 *
 * É medida do corpus, não tabela escrita à mão: a dificuldade sai do português
 * que este jogo realmente usa. Conta as candidatas automáticas (3 a 10 letras,
 * §4.2), que são um recorte do dicionário de validação — proxy da dificuldade
 * real de lembrar uma palavra com aquela letra, e o único dado de frequência
 * que o índice expõe.
 */
export function alcanceDaLetra(indice: IndiceLexico, letra: Caractere): number {
  let total = 0;
  for (const posicao of indice.posicoesDe(letra)) {
    total += indice.quantidade(letra, posicao);
  }
  return total;
}

function maiorAlcance(indice: IndiceLexico): number {
  let maior = 0;
  for (const letra of ALFABETO) {
    const alcance = alcanceDaLetra(indice, paraCaractere(letra));
    if (alcance > maior) maior = alcance;
  }
  return maior;
}

/**
 * Multiplicador da aposta feita ao escolher esta letra como âncora.
 *
 * Entre 1 (letra comum) e 5 (letra que quase não aparece). Com índice vazio —
 * dicionário ainda carregando — devolve o piso, nunca um prêmio de graça.
 */
export function multiplicadorDaAncora(indice: IndiceLexico, letra: Caractere): number {
  const referencia = maiorAlcance(indice);
  if (referencia === 0) return MULTIPLICADOR_MINIMO;

  const fracao = alcanceDaLetra(indice, letra) / referencia;
  for (const faixa of FAIXAS) {
    if (fracao >= faixa.piso) return faixa.multiplicador;
  }
  return MULTIPLICADOR_MAXIMO;
}

/**
 * Quantas palavras já colocadas a nova atravessa.
 *
 * A grade tem de ser a de **antes** de colocá-la. Da segunda jogada em diante
 * o mínimo é 1 — a ponte de onde saiu a âncora —, e é o que passa disso que o
 * modo premia.
 */
export function cruzamentosDe(grade: Grade, palavra: PalavraColocada): number {
  const atravessadas = new Set<IdPalavra>();
  const letras = caracteresCanonicos(palavra.texto);

  for (let i = 0; i < letras.length; i += 1) {
    const celula = celulaNoIndice(palavra.origem, palavra.orientacao, i);
    for (const id of grade.palavrasEm(celula)) atravessadas.add(id);
  }
  return atravessadas.size;
}

export interface PontosDaJogada {
  readonly letras: number;
  readonly cruzamentos: number;
  /** Travessias além da obrigatória — o que de fato paga. */
  readonly extras: number;
  readonly multiplicador: number;
  readonly pontos: number;
}

/**
 * Pontos de uma jogada confirmada.
 *
 * Conta só a palavra-base: a vertical veio do sistema, e premiar o tamanho
 * dela seria pagar o jogador pelo acaso da sugestão.
 */
export function pontuarJogada(
  letras: number,
  cruzamentos: number,
  multiplicador: number,
): PontosDaJogada {
  const extras = Math.max(0, cruzamentos - 1);
  const pontos = (letras + BONUS_CRUZAMENTO * extras) * multiplicador;
  return { letras, cruzamentos, extras, multiplicador, pontos };
}

/** Jogadas confirmadas: cada uma acrescenta duas palavras, a base e a ponte. */
export function jogadasDe(cadeia: Cadeia): number {
  return Math.floor(cadeia.palavras.length / 2);
}

export function jogadasRestantes(cadeia: Cadeia): number {
  return Math.max(0, JOGADAS_TRAMA - jogadasDe(cadeia));
}

export function partidaTerminou(cadeia: Cadeia): boolean {
  return jogadasDe(cadeia) >= JOGADAS_TRAMA;
}
